import React, { useRef, useState, useEffect } from 'react';
import { Box, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Link, Paper } from '@mui/material';
import { indigo } from '@mui/material/colors';
import get from 'lodash/get';
import { orderBy as lodashOrderBy } from 'lodash';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { generatePath, useHistory } from 'react-router-dom';
import TableToCSVGenerator from '../../TableToCSVGenerator';
import { formatNumber, formatValueOfMeals, csvFileNameGenerators } from '../../../helpers/formatters';
import { Colors } from '../../../assets/theme/Colors';
import { formatDateToMonthName, formatDateToWeekName } from '../../../helpers/dates';
import { getUserFullName } from '../../../helpers/user';
import routes from '../../../routes';

const paperSx = {
  padding: { sx: 1, sm: 2 },
  '& table': {
    '& thead': {
      '& th': {
        fontWeight: 600,
        padding: '3px 12px',
        whiteSpace: 'nowrap',
      },
    },
    '& tbody': {
      '& td': {
        padding: '3px 12px',
        borderBottom: 'none',
      },
      '& td:first-child': {
        minWidth: '200px',
      },
    },
  },
};
const rowTotalSx = {
  backgroundColor: 'primary.main',
  color: '#fff',
  fontWeight: 600,
};
const footerTotalColumnSx = {
  left: 0,
  position: 'sticky',
};
const oddColumnSx = {
  background: `${indigo[50]} !important`,
};
const diffOddSx = {
  color: `${Colors.tableDiff.odd.contrastText} !important`,
  boxShadow: `inset 0px 0px 1px 100px ${Colors.tableDiff.odd.color}`,
};
const diffEvenSx = {
  color: `${Colors.tableDiff.even.contrastText} !important`,
  boxShadow: `inset 0px 0px 1px 100px ${Colors.tableDiff.even.color}`,
};
const periodStickyHeaderSx = {
  top: 0,
  position: 'sticky',
  zIndex: 1,
  background: '#fff',
};
const entityTypeStickyHeaderSx = {
  top: 36,
  position: 'sticky',
  zIndex: 1,
  background: '#fff',
};
const siteColumnHeadSx = {
  position: 'sticky',
  top: 0,
  left: 0,
  background: '#fff',
  zIndex: 2,
  boxShadow: 'inset 0 -2px 0px 0px #7986cb',
};
const siteColumnSx = {
  maxWidth: '200px',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  position: 'sticky',
  left: 0,
  background: '#fff',
};
const siteDirectorColumnHeadSx = {
  position: 'sticky',
  top: 0,
  left: '200px',
  background: '#fff',
  boxShadow: '2px 0 0px 0px #7986cb, inset 0 -2px 0px 0px #7986cb',
  zIndex: 2,
};
const siteDirectorColumnSx = {
  maxWidth: '200px',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  position: 'sticky',
  left: 200,
  background: '#fff',
  boxShadow: '2px 0 0px 0px #7986cb',
};
const sortByCellSx = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  cursor: 'pointer',
};
const justifyContentCenterSx = {
  justifyContent: 'center',
};
const descriptionsHeaderSx = {
  borderBottom: 'none',
};
const tableContainerSx = {
  height: 'calc(100vh - 400px)',
  overflow: 'scroll',
  '&::-webkit-scrollbar': {
    WebkitAppearance: 'none',
    width: '0.65rem',
    height: '0.65rem',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 4,
    backgroundColor: 'rgba(0, 0, 0, .5)',
    WebkitBoxShadow: '0 0 1px rgba(255, 255, 255, .5)',
  },
  '@media (max-height: 680px)': {
    height: '50vh',
  },
};
const borderLeftColumnSx = {
  borderLeft: `2px solid ${indigo[300]}`,
};
const rescueSizeColumnSx = {
  whiteSpace: 'nowrap',
  position: 'sticky',
  left: 361,
  background: '#fff',
  boxShadow: '2px 0 0px 0px #7986cb',
};
const rescueSizeColumnHeadSx = {
  position: 'sticky',
  top: 0,
  left: 361,
  background: '#fff',
  boxShadow: '2px 0 0px 0px #7986cb, inset 0 -2px 0px 0px #7986cb',
  zIndex: 2,
};

const RescueReportTable = ({ filters, rescueReportData }) => {
  const tableRef = useRef();
  const history = useHistory();
  const groupByRescueSize = get(filters, 'byRescueSize', false);
  const granularity = get(filters, 'granularity');
  const [rescueReport, setRescueReport] = useState(rescueReportData);

  // Generate dynamic filename based on date range
  const generateCsvFileName = () => {
    return csvFileNameGenerators.rescueReport({
      dateRange: {
        startDate: filters?.startDate,
        endDate: filters?.endDate
      }
    });
  };

  useEffect(() => {
    setRescueReport(rescueReportData);
  }, [rescueReportData]);

  const [orderBy, setOrderBy] = useState({
    period: null,
    entityType: 'site',
    measure: null,
    order: 'asc',
  });

  const handleSortBy = (period, entityType, measure) => {
    const foundOrderBy =
      orderBy.period === period && orderBy.entityType === entityType && orderBy.measure === measure
        ? orderBy.order
        : null;
    const order = foundOrderBy ? (foundOrderBy === 'asc' ? 'desc' : 'asc') : 'asc';

    const saveOrders = () =>
      setOrderBy({
        period: period,
        entityType: entityType,
        measure: measure,
        order: order,
      });

    if (['site', 'site_director'].indexOf(entityType) >= 0) {
      setRescueReport({
        ...rescueReport,
        sites: lodashOrderBy(
          rescueReport.sites,
          row => {
            if (entityType === 'site') {
              return row.site.name;
            }

            if (entityType === 'site_director') {
              return getUserFullName(row.site.site_director);
            }
          },
          order
        ),
      });

      return saveOrders();
    }

    if (groupByRescueSize) {
      setRescueReport({
        ...rescueReport,
        sites: rescueReport.sites.map((siteRow, siteIndex) => {
          if (measure !== null) {
            if (period !== null) {
              return {
                ...siteRow,
                rescue_sizes: lodashOrderBy(
                  siteRow.rescue_sizes,
                  rescueSizeRow => {
                    const value = rescueSizeRow[entityType].periods[period][measure];
                    return value !== null ? value : -1;
                  },
                  order
                ),
              };
            }

            // period is not set, we want to sort by row total
            const value = siteIndex[entityType].total[measure];

            return value !== null ? value : -1;
          }

          if (entityType === 'rescue_size') {
            return {
              ...siteRow,
              rescue_sizes: lodashOrderBy(siteRow.rescue_sizes, rescueSizeRow => rescueSizeRow.rescue_size.name, order),
            };
          }

          return siteRow;
        }),
      });

      return saveOrders();
    }

    setRescueReport({
      ...rescueReport,
      sites: lodashOrderBy(
        rescueReport.sites,
        row => {
          if (measure !== null) {
            if (period !== null) {
              const value = row.total[entityType].periods[period][measure];

              return value !== null ? value : -1;
            }

            // period is not set, we want to sort by row total
            const value = row[entityType].total[measure];

            return value !== null ? value : -1;
          }

          if (entityType === 'rescue_size') {
            return row.total[entityType].periods[period][measure];
          }

          return row.site.name;
        },
        order
      ),
    });

    return saveOrders();
  };

  const renderSortBy = (index, entityType, label, measure) => (
    <Box
      onClick={() => handleSortBy(index, entityType, measure)}
      sx={[sortByCellSx, entityType !== 'site' && entityType !== 'siteDirector' && justifyContentCenterSx]}
    >
      <>
        <span>{label !== null && label}</span>

        {orderBy.period === index && orderBy.entityType === entityType && (
          <>{orderBy.order === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}</>
        )}
      </>
    </Box>
  );

  const renderCount = (count, format = false) => {
    if (format === 'value') {
      return formatValueOfMeals(count);
    }

    return formatNumber(count);
  };

  const renderEntityColumn = (index, entityName, entityType, showBorder = false, isOdd = null) => (
    <TableCell
      variant="head"
      align="center"
      sx={[
        entityTypeStickyHeaderSx,
        descriptionsHeaderSx,
        isOdd === true && diffOddSx,
        isOdd === false && diffEvenSx,
        showBorder && borderLeftColumnSx,
      ]}
    >
      {renderSortBy(index, entityType, entityName, 'count')}
    </TableCell>
  );

  const renderRow = (index, row) => {
    const _renderRowEntity = (index, index2, data, format = 'number', showBorder = false, isOdd = null) => (
      <>
        <TableCell
          variant="head"
          align="center"
          sx={[
            index % 2 && oddColumnSx,
            isOdd === true && diffOddSx,
            isOdd === false && diffEvenSx,
            showBorder && borderLeftColumnSx,
          ]}
          data-period={index2}
        >
          {renderCount(data.count, format)}
        </TableCell>
      </>
    );

    return (
      <TableRow>
        <TableCell
          sx={[siteColumnSx, index % 2 && oddColumnSx]}
        >
          {row.site.name}
        </TableCell>

        <TableCell
          sx={[siteDirectorColumnSx, index % 2 && oddColumnSx]}
        >
          {row.site.site_director && (
            <Link
              onClick={() => history.push(generatePath(routes.userEditNew, { id: row.site.site_director.id }))}
              style={{ cursor: 'pointer' }}
              underline="hover"
            >
              {getUserFullName(row.site.site_director)}
            </Link>
          )}

          {!row.site.site_director && 'n/a'}
        </TableCell>
        {get(rescueReport, 'periods', []).map((period, index2) => (
          <React.Fragment key={index2}>
            {_renderRowEntity(index, index2, row.total.rescues.periods[index2], 'number', true)}
            {_renderRowEntity(index, index2, row.total.meals.periods[index2])}
            {_renderRowEntity(index, index2, row.total.lbs.periods[index2])}
            {_renderRowEntity(index, index2, row.total.value.periods[index2], 'value')}
          </React.Fragment>
        ))}
      </TableRow>
    );
  };
  const renderRescueSizesRow = (siteIndex, siteRow) => {
    const rescueSizesList = siteRow.rescue_sizes || [];

    if (rescueSizesList.length === 0) {
      return null;
    }

    const _renderRowEntity = (index, periodIndex, data, showBorder = false, isOdd = null) => (
      <TableCell
        variant="head"
        align="center"
        sx={[
            index % 2 && oddColumnSx,
            isOdd === true && diffOddSx,
            isOdd === false && diffEvenSx,
            showBorder && borderLeftColumnSx,
          ]}
        data-period={periodIndex}
      >
        {renderCount(data.count)}
      </TableCell>
    );

    return rescueSizesList.map((rescueSize, rescueSizeIndex) => (
      <React.Fragment key={`${siteIndex}-${rescueSizeIndex}`}>
        <TableRow>
          {rescueSizeIndex === 0 && (
            <>
              <TableCell
                rowSpan={siteRow.rescue_sizes.length}
                sx={[siteColumnSx, siteIndex % 2 && oddColumnSx]}
              >
                {siteRow.site.name}
              </TableCell>
              <TableCell
                rowSpan={siteRow.rescue_sizes.length}
                sx={[siteDirectorColumnSx, siteIndex % 2 && oddColumnSx]}
              >
                {siteRow.site.site_director && (
                  <Link
                    onClick={() =>
                      history.push(generatePath(routes.userEditNew, { id: siteRow.site.site_director.id }))
                    }
                    style={{ cursor: 'pointer' }}
                    underline="hover"
                  >
                    {getUserFullName(siteRow.site.site_director)}
                  </Link>
                )}

                {!siteRow.site.site_director && 'n/a'}
              </TableCell>
            </>
          )}

          <TableCell
            sx={[rescueSizeColumnSx, siteIndex % 2 && oddColumnSx]}
          >
            {rescueSize.rescue_size.name}
          </TableCell>

          {rescueReport.periods.map((period, periodIndex) => (
            <React.Fragment key={periodIndex}>
              {_renderRowEntity(siteIndex, periodIndex, rescueSize.rescues.periods[periodIndex], true)}
              {_renderRowEntity(siteIndex, periodIndex, rescueSize.meals.periods[periodIndex])}
              {_renderRowEntity(siteIndex, periodIndex, rescueSize.lbs.periods[periodIndex])}
              {_renderRowEntity(siteIndex, periodIndex, rescueSize.value.periods[periodIndex])}
            </React.Fragment>
          ))}

          {/*{_renderRowEntityTotal(index, siteRow.total)}*/}
        </TableRow>
      </React.Fragment>
    ));
  };

  const renderTotalRow = totalData => {
    const _renderTotalRowEntityPeriod = (index, sitetotalDataEntityTotalPeriod, showBorder = false) => (
      <>
        <TableCell
          variant="head"
          align="center"
          sx={[rowTotalSx, showBorder && borderLeftColumnSx]}
        >
          {renderCount(sitetotalDataEntityTotalPeriod.count)}
        </TableCell>
      </>
    );

    return (
      <TableRow>
        <TableCell
          colSpan={groupByRescueSize ? 3 : 2}
          variant="head"
          sx={[rowTotalSx, footerTotalColumnSx]}
        >
          Total
        </TableCell>

        {rescueReport.periods.map((row, index) => (
          <React.Fragment key={index}>
            {_renderTotalRowEntityPeriod(index, totalData.rescues.periods[index], true)}

            {_renderTotalRowEntityPeriod(index, totalData.meals.periods[index])}

            {_renderTotalRowEntityPeriod(index, totalData.lbs.periods[index])}

            {_renderTotalRowEntityPeriod(index, totalData.value.periods[index])}
          </React.Fragment>
        ))}
      </TableRow>
    );
  };

  if (!rescueReportData.sites.length) {
    return (
      <Box my={2}>
        <Paper sx={paperSx}>Please use "RUN" button to generate the report.</Paper>
      </Box>
    );
  }

  return (
    <Box my={2}>
      <Paper sx={paperSx}>
        <TableToCSVGenerator tableRef={tableRef} fileName={generateCsvFileName()} />
        <TableContainer sx={tableContainerSx} className="table-container">
          <Table aria-label="spanning table" size="small" ref={tableRef} id="rescue-report-table">
            <TableHead>
              <TableRow>
                <TableCell
                  rowSpan={2}
                  sx={[descriptionsHeaderSx, siteColumnHeadSx]}
                >
                  {renderSortBy(null, 'site', 'Site', null)}
                </TableCell>

                <TableCell
                  rowSpan={2}
                  sx={[descriptionsHeaderSx, siteDirectorColumnHeadSx]}
                >
                  {renderSortBy(null, 'siteDirector', 'Site Director', null)}
                </TableCell>

                {groupByRescueSize && (
                  <TableCell
                    rowSpan={2}
                    sx={[descriptionsHeaderSx, rescueSizeColumnHeadSx]}
                  >
                    {renderSortBy(null, 'rescue_size', 'Rescue Vehicle Size', null)}
                  </TableCell>
                )}

                {rescueReport.periods.map((row, index) => (
                  <React.Fragment key={index}>
                    <TableCell
                      colSpan={4}
                      variant="head"
                      align="center"
                      sx={[periodStickyHeaderSx, descriptionsHeaderSx, borderLeftColumnSx]}
                    >
                      {granularity === 'monthly' ? formatDateToMonthName(row) : formatDateToWeekName(row)}
                    </TableCell>
                  </React.Fragment>
                ))}
              </TableRow>

              <TableRow>
                {rescueReport.periods.map((row, index) => (
                  <React.Fragment key={index}>
                    {renderEntityColumn(index, '# Rescues', 'rescues', true)}

                    {renderEntityColumn(index, '# Meals', 'meals')}

                    {renderEntityColumn(index, '# Lbs', 'lbs')}

                    {renderEntityColumn(index, '$', 'value')}
                  </React.Fragment>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {groupByRescueSize
                ? rescueReport.sites.map((siteRow, siteIndex) => (
                    <React.Fragment key={siteIndex}>{renderRescueSizesRow(siteIndex, siteRow)}</React.Fragment>
                  ))
                : rescueReport.sites.map((row, index) => (
                    <React.Fragment key={index}>{renderRow(index, row)}</React.Fragment>
                  ))}

              {renderTotalRow(rescueReport.total)}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default RescueReportTable;
